import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useState,
} from 'react';

export type ComboboxOption =
  | { kind: 'suggestion'; value: string; displayText: string }
  | { kind: 'create'; value: string; displayText: string };

export interface UseTagComboboxArgs {
  draft: string;
  options: ComboboxOption[];
  disabled: boolean;
  /**
   * Called when the user picks (Enter / click) — argument is the option's
   * `value`, or the typed `draft` when no option is active.
   */
  onCommit: (text: string) => void;
  /** Backspace on an empty input removes the last user-added chip. */
  onRemoveLast: () => void;
  /** Second Escape clears the draft. */
  onClearDraft: () => void;
}

export function useTagCombobox({
  draft,
  options,
  disabled,
  onCommit,
  onRemoveLast,
  onClearDraft,
}: UseTagComboboxArgs) {
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const optionId = useCallback(
    (index: number) => `${baseId}-opt-${index}`,
    [baseId]
  );

  // Keep activeIndex in range when option list shrinks under it.
  useEffect(() => {
    setActiveIndex((idx) => (idx >= options.length ? options.length - 1 : idx));
  }, [options.length]);

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (options.length === 0) return;
        setOpen(true);
        setActiveIndex((idx) => (idx + 1) % options.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (options.length === 0) return;
        setOpen(true);
        setActiveIndex((idx) => (idx <= 0 ? options.length - 1 : idx - 1));
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        const active = activeIndex >= 0 ? options[activeIndex] : undefined;
        const text = active ? active.value : draft;
        onCommit(text);
        setActiveIndex(-1);
        return;
      }

      if (event.key === 'Escape') {
        if (open) {
          event.preventDefault();
          close();
        } else if (draft !== '') {
          event.preventDefault();
          onClearDraft();
        }
        return;
      }

      if (event.key === 'Tab') {
        if (open) close();
        return;
      }

      if (event.key === 'Backspace' && draft === '') {
        onRemoveLast();
      }
    },
    [
      activeIndex,
      close,
      disabled,
      draft,
      onClearDraft,
      onCommit,
      onRemoveLast,
      open,
      options,
    ]
  );

  const openPopover = useCallback(() => {
    if (!disabled) setOpen(true);
  }, [disabled]);

  const selectOption = useCallback(
    (index: number) => {
      const opt = options[index];
      if (!opt) return;
      onCommit(opt.value);
      setActiveIndex(-1);
    },
    [onCommit, options]
  );

  return {
    open,
    setOpen,
    openPopover,
    close,
    activeIndex,
    setActiveIndex,
    listboxId,
    optionId,
    handleKeyDown,
    selectOption,
  };
}
